<script setup>
import { computed, ref, watch } from 'vue';
import { usePlayers } from '../composables/usePlayers.js';
import { useAuth } from '../composables/useAuth.js';
import PlayerRow from './PlayerRow.vue';
import SquadSearch from './SquadSearch.vue';
import ListControls from './ListControls.vue';
import ListSections from './ListSections.vue';
import PlayerColumn from './PlayerColumn.vue';
import YearSelect from './YearSelect.vue';
import AgeGroupFilter from './AgeGroupFilter.vue';
import FilterToggle from './FilterToggle.vue';
import FilterBox from './FilterBox.vue';
import { showAgeGroupFilter } from '../domain/ageGroups.js';
import SelectToggle from './SelectToggle.vue';
import { flattenSections } from '../domain/profileNav.js';
import { useSelection } from '../composables/useSelection.js';

const {
  query, filteredSquad, squadSections, released,
  positionFilter, sortMode, positionCounts,
  yearFilter, activeYear, years, yearCounts, ageDirection,
  ageGroupFilter, ageGroupCounts, anyAgeGroup, activeFilterCount, resetFilters,
  loading, error, open,
} = usePlayers();
const { can, hidesInaccessibleAreas } = useAuth();
/* Filter Altersklasse: Bearbeiter immer, alle anderen (staff, Gäste) nur wenn es eine Zuordnung gibt (F16: Gäste wie staff). */
const showAgeFilter = computed(() => showAgeGroupFilter(can('players', 'write'), anyAgeGroup.value));
const { canSelect, done: endSelection } = useSelection();

/* Verliert die Rolle das Recht bei offener App (Gast-Wechsel), endet der Markier-Modus */
watch(canSelect, (ok) => {
  if (!ok) endSelection();
});

/* Profil öffnen und die Liste mitgeben, aus der geöffnet wurde (domain/profileNav.js): Kader = die
   angezeigte Reihenfolge (Filter, Suche, Sortierung, Abschnitte nacheinander), Freigegeben = diese Spalte. */
const openFromSquad = (id) => open(id, null, flattenSections(squadSections.value));
const openFromReleased = (id) => open(id, null, released.value.map((p) => p.id));

defineEmits(['add']);

/* Jahrgangsknöpfe: "Alle Jahrgänge" + je ein Knopf pro Jahrgang im Kader (jüngster zuerst), erzeugt in
   usePlayers.js aus den geladenen Spielern. Gibt es den gewählten Jahrgang nicht mehr (letzter Spieler
   weg), fällt die Wahl auf "Alle" zurück. */
/* Filter-Bereich: beim Laden immer zu, Zustand wird nicht gespeichert. Die Filter wirken auch bei geschlossenem
   Bereich; die Zahl am Knopf zeigt, wie viele aktiv sind (Position, Jahrgang, Altersklasse — nicht Sortierung, nicht Suche). */
const filtersOpen = ref(false);
const FILTER_PANEL_ID = 'squad-filter-panel';

const YEAR_KEYS = computed(() => ['all', ...years.value]);
watch(activeYear, (y) => {
  if (yearFilter.value !== y) yearFilter.value = y;
});
</script>

<template>
  <div id="squad-area">
    <!-- Dekorative Trennlinie zwischen Kopfbereich und Inhalt -->
    <div class="branch" aria-hidden="true"></div>

    <!-- Die Zugangsanfragen standen hier bis 2026-08-26 als aufklappbarer
         Kasten. Sie liegen jetzt in der Nutzerverwaltung hinter dem
         Konto-Knopf; dass es etwas zu entscheiden gibt, meldet der Zaehler
         dort. Siehe AccountMenu.vue. -->

    <!-- Obere Zeile: Suche und Knopf "Filter". Darunter (Handy) die Spielerzahl und "Select"; auf dem Laptop stehen
         Suche, Filter und Select in einer Zeile, die Spielerzahl rechts davon. -->
    <SquadSearch>
      <FilterToggle v-model="filtersOpen" :count="activeFilterCount" :panel-id="FILTER_PANEL_ID" />
      <div class="list-meta">
        <span class="shown-count" data-testid="shown-count" aria-live="polite">{{ $t('squad.shownCount', { count: filteredSquad.length }, filteredSquad.length) }}</span>
        <SelectToggle />
      </div>
    </SquadSearch>

    <!-- Filter-Bereich (Standard: zu). Reihenfolge: Position, Sortierung, Jahrgang, Altersklasse, Reset. -->
    <FilterBox :open="filtersOpen" :count="activeFilterCount" :id="FILTER_PANEL_ID" @reset="resetFilters">
      <ListControls
        v-model:position="positionFilter"
        v-model:sort="sortMode"
        v-model:direction="ageDirection"
        :counts="positionCounts"
      />

      <!-- Jahrgang: ein Auswahlfeld im Chip-Stil (YearSelect.vue). Gilt zusammen mit Suche und Position (UND);
           die Zahlen berücksichtigen den jeweils anderen Filter. -->
      <div v-if="years.length" class="year-group" data-testid="year-filter">
        <YearSelect v-model="yearFilter" :keys="YEAR_KEYS" :counts="yearCounts" />
      </div>

      <!-- Altersklasse: Alle / U17 / U19. Für Leser nur sichtbar, wenn es überhaupt eine Zuordnung gibt. -->
      <AgeGroupFilter v-if="showAgeFilter" v-model="ageGroupFilter" :counts="ageGroupCounts" />
    </FilterBox>

    <div class="branches">
      <div v-if="error" class="empty-col error">{{ $t(`errors.${error}`) }}</div>
      <div v-else-if="loading" class="empty-col">{{ $t('common.loading') }}</div>

      <template v-else>
        <PlayerColumn
          :title="$t('squad.headline')"
          :description="$t('squad.headlineDesc')"
          :count="filteredSquad.length"
          sr-only-head
        >
          <div v-if="!squadSections.length" class="empty-col">
            {{ query || activeFilterCount ? $t('squad.empty') : $t('squad.noPlayers') }}
          </div>
          <ListSections :sections="squadSections" :sort="sortMode">
            <template #member="{ player }">
              <PlayerRow :player="player" selectable @open="openFromSquad" />
            </template>
          </ListSections>
        </PlayerColumn>

        <!-- Freigegeben startet zugeklappt; der Zustand wird pro Gerät gemerkt. Die Liste ist von der
             Suche entkoppelt, siehe usePlayers.js — die Anzahl in der Kopfzeile ist die Gesamtzahl, nicht
             die Trefferzahl. Die Transferierten stehen auf einer eigenen Seite (TransferredList.vue). -->
        <PlayerColumn
          v-if="released.length"
          :title="$t('squad.releasedTitle')"
          :description="$t('squad.releasedDesc')"
          :count="released.length"
          collapsible
          storage-key="grh_squadOpen_released"
        >
          <PlayerRow v-for="p in released" :key="p.id" :player="p" @open="openFromReleased" />
        </PlayerColumn>
      </template>
    </div>

    <!-- Ohne Schreibrecht bleibt der Knopf sichtbar, ist aber gesperrt —
         sonst wirkt es, als gäbe es die Funktion gar nicht. -->
    <div v-if="can('players', 'write') || !hidesInaccessibleAreas" class="add-player-bottom">
      <button
        class="btn"
        :disabled="!can('players', 'write')"
        :title="can('players', 'write') ? '' : $t('sector.lockedGroup')"
        @click="$emit('add')"
      >
        + {{ $t('squad.addPlayer') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Zeile unter der Suche (Handy): Spielerzahl links, Select rechts; Laptop: Zahl weg, Select neben dem Filter-Knopf */
.list-meta {
  flex: 1 1 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}
.shown-count {
  font-size: var(--fs-xs);
  font-weight: 500;
  color: var(--muted);
  min-width: 0;
  overflow-wrap: anywhere;
}
/* Laptop: Suche, Filter und Select in einer Zeile, die Spielerzahl rechts am Rand der Inhaltsbreite (F3) */
@media (min-width: 601px) {
  .list-meta {
    display: contents;
  }
  .shown-count {
    order: 2;
    margin-left: auto;
    padding-right: 2px;
    white-space: nowrap;
  }
}
.year-group {
  flex: 1 0 100%;
  display: flex;
}
.empty-col.error {
  color: var(--red);
}
</style>
